const organisationSearchResults = {
    init() {
        this.initSummaryTabs();
        this.initSummaryTransactionView();
        this.initAjaxResults();
    },

    initSummaryTransactionView() {
        const root = document.querySelector('[data-organisation-search-transaction-views]');
        if (!root) {
            return;
        }

        const buttons = Array.from(document.querySelectorAll('[data-organisation-search-transaction-view-button]'));
        const panels = Array.from(root.querySelectorAll('[data-organisation-search-transaction-view]'));
        const storageKey = 'organisation-search-transaction-view';

        const setView = (requestedView, persist = false) => {
            const view = ['table', 'list'].includes(requestedView) ? requestedView : 'table';

            buttons.forEach((button) => {
                const isActive = button.dataset.organisationSearchTransactionViewButton === view;
                button.classList.toggle('is-active', isActive);
                button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
            panels.forEach((panel) => {
                panel.hidden = panel.dataset.organisationSearchTransactionView !== view;
            });
            root.dataset.activeView = view;

            if (persist) {
                try {
                    window.localStorage.setItem(storageKey, view);
                } catch (error) {
                    // Storage can be unavailable in private or restricted browser contexts.
                }
            }
        };

        let initialView = 'table';
        try {
            initialView = window.localStorage.getItem(storageKey) || 'table';
        } catch (error) {
            initialView = 'table';
        }

        buttons.forEach((button) => {
            button.addEventListener('click', () => {
                setView(button.dataset.organisationSearchTransactionViewButton, true);
            });
        });

        setView(initialView);
    },

    initSummaryTabs() {
        const tabsRoot = document.querySelector('[data-organisation-search-tabs]');
        const panelsRoot = document.querySelector('[data-organisation-search-tab-panels]');

        if (!tabsRoot || !panelsRoot) {
            return;
        }

        const tabs = Array.from(tabsRoot.querySelectorAll('[data-organisation-search-tab]'));
        const panels = Array.from(panelsRoot.querySelectorAll('[data-organisation-search-group]'));
        const emptyState = panelsRoot.querySelector('[data-organisation-search-tab-empty]');
        const allowedTabs = tabs.map((tab) => tab.dataset.organisationSearchTab);

        const setTab = (requestedTab, updateHistory = false) => {
            const tabKey = allowedTabs.includes(requestedTab) ? requestedTab : 'all';
            let visibleCount = 0;

            tabs.forEach((tab) => {
                const isActive = tab.dataset.organisationSearchTab === tabKey;
                tab.classList.toggle('is-active', isActive);
                tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
                tab.setAttribute('tabindex', isActive ? '0' : '-1');
            });

            panels.forEach((panel) => {
                const isVisible = tabKey === 'all' || panel.dataset.organisationSearchGroup === tabKey;
                panel.hidden = !isVisible;
                if (isVisible) {
                    visibleCount += 1;
                }
            });

            if (emptyState) {
                emptyState.hidden = visibleCount > 0;
            }

            tabsRoot.dataset.activeTab = tabKey;

            if (updateHistory) {
                const url = new URL(window.location.href);
                url.searchParams.set('tab', tabKey);
                window.history.pushState({ organisationSearchTab: tabKey }, '', url);
            }
        };

        tabsRoot.addEventListener('click', (event) => {
            const tab = event.target.closest('[data-organisation-search-tab]');
            if (!tab || !tabsRoot.contains(tab)) {
                return;
            }

            event.preventDefault();
            if (tabsRoot.dataset.activeTab === tab.dataset.organisationSearchTab) {
                return;
            }
            setTab(tab.dataset.organisationSearchTab, true);
        });

        tabsRoot.addEventListener('keydown', (event) => {
            if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) {
                return;
            }

            const currentIndex = tabs.findIndex((tab) => tab.classList.contains('is-active'));
            const direction = event.key === 'ArrowRight' ? 1 : -1;
            const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
            event.preventDefault();
            tabs[nextIndex].focus();
            setTab(tabs[nextIndex].dataset.organisationSearchTab, true);
        });

        window.addEventListener('popstate', () => {
            setTab(new URL(window.location.href).searchParams.get('tab') || 'all');
        });

        setTab(tabsRoot.dataset.activeTab || 'all');
    },

    initAjaxResults() {
        const root = document.querySelector('[data-organisation-search-results-page]');
        const content = root?.querySelector('[data-organisation-search-results-content]');
        const form = root?.querySelector('[data-organisation-search-results-form]');

        if (!root || !content || !form) {
            return;
        }

        const basePath = window.location.pathname;
        let activeRequest = null;

        const syncForm = (url) => {
            Array.from(form.elements).forEach((control) => {
                if (!control.name) {
                    return;
                }
                control.value = url.searchParams.get(control.name) || '';
            });

            const queryLabel = root.querySelector('[data-organisation-search-query-label]');
            if (queryLabel) {
                queryLabel.textContent = url.searchParams.get('query') || '';
            }
        };

        const loadResults = async (target, updateHistory = true) => {
            const url = new URL(target, window.location.origin);

            if (activeRequest) {
                activeRequest.abort();
            }

            const request = new AbortController();
            activeRequest = request;
            root.classList.add('is-loading-results');
            content.setAttribute('aria-busy', 'true');

            try {
                const response = await fetch(url, {
                    credentials: 'same-origin',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-Organisation-Search-Fragment': 'results',
                    },
                    signal: request.signal,
                });

                if (!response.ok) {
                    throw new Error(`Search results request failed with ${response.status}`);
                }

                content.innerHTML = await response.text();
                syncForm(url);

                if (updateHistory) {
                    window.history.pushState({ organisationSearchResults: true }, '', url);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    window.location.assign(url.toString());
                }
            } finally {
                if (activeRequest === request) {
                    root.classList.remove('is-loading-results');
                    content.setAttribute('aria-busy', 'false');
                    activeRequest = null;
                }
            }
        };

        root.addEventListener('click', (event) => {
            const link = event.target.closest('.organisation-search-pagination a.page-link');
            if (!link || !root.contains(link)) {
                return;
            }

            event.preventDefault();
            loadResults(link.href);
        });

        form.addEventListener('submit', (event) => {
            event.preventDefault();
            const url = new URL(form.action, window.location.origin);

            new FormData(form).forEach((value, key) => {
                const normalizedValue = String(value).trim();
                if (normalizedValue !== '') {
                    url.searchParams.set(key, normalizedValue);
                }
            });

            loadResults(url);
        });

        form.querySelector('[name="type"]')?.addEventListener('change', () => {
            form.requestSubmit();
        });

        window.addEventListener('popstate', () => {
            if (window.location.pathname !== basePath) {
                window.location.reload();
                return;
            }

            loadResults(window.location.href, false);
        });
    },
};

export default organisationSearchResults;
